import { useFlag } from '@unleash/proxy-client-react'
import { RefreshCcw, Badge as UpdatedBadgeIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion'
import AiButton from '@/components/ui/ai-button'
import { Button } from '@/components/ui/button'
import BlackStarIcon from '@/components/ui/icons/black-star-icon'
import InfoRounded from '@/components/ui/icons/info-rounded'
import LoadingSpinner from '@/components/ui/icons/loading-spinner'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { useConsultationAISummary } from '@/hooks/useConsultationAISummary'
import { useCurrentMedication } from '@/hooks/useCurrentMedication'
import { useQuestionnaire } from '@/hooks/useQuestionnaire'
import { CARE_FIXTURES_ENABLED } from '@/lib/care-fixtures'
import { cn, getAiRespTextClassName, useConsultationSummaryFlag } from '@/lib/utils'
import { ConsultationTable } from '../consultation-table'
import CareConsultation from './care-consultation'

export function SuggestedActionBadge({ suggestedActionTag }: { suggestedActionTag: string }) {
  if (!['HUMAN REVIEW', 'REJECT', 'APPROVE', 'PLACE ON HOLD'].includes(suggestedActionTag)) {
    return null
  }

  const variants: Record<string, string> = {
    'HUMAN REVIEW': 'bg-gray-50 border-gray-500 text-gray-500',
    REJECT: 'bg-red-50 border-red-500 text-red-500',
    APPROVE: 'bg-green-50 border-green-500 text-green-500',
    'PLACE ON HOLD': 'bg-yellow-50 border-yellow-500 text-yellow-500'
  }
  const actionTagClassName = variants[suggestedActionTag] ?? ''

  return (
    <div className={cn('px-3 py-1.5 rounded-[8px] font-bold text-sm font-poppins border', actionTagClassName)}>
      {suggestedActionTag}
    </div>
  )
}

export default function Consultation() {
  const medRequest = useCurrentMedication()
  const { parsedQuestions } = useQuestionnaire(medRequest?.data?.questionaire ?? '')
  // Care check-ins are a separate instrument from the treatment consultation, and their data is
  // still landing (see `services/care-definitions`), so the panel stays behind a flag — except in
  // an environment that opted into sample data, where showing the panel is the point of opting in.
  const isCareVisualisationEnabled = useFlag('care-visualisation') || CARE_FIXTURES_ENABLED

  const { enabled: isConsultationSummaryEnabled } =
    useConsultationSummaryFlag(medRequest?.data) || !!import.meta.env.DEV
  const [consultationDataAccordionValue, setConsultationDataAccordionValue] = useState<string | undefined>(
    !isConsultationSummaryEnabled ? 'consultation-data' : undefined
  )
  const [showOnlyUpdated, setShowOnlyUpdated] = useState(false)

  const hasUpdatedQuestions = !!parsedQuestions?.some(question => question.updated)

  const {
    isFetching,
    invalidate,
    mainText,
    attentionText,
    suggestedAction,
    suggestedActionTag,
    suggestedDescription,
    consultationError
  } = useConsultationAISummary(medRequest.data)

  const withoutSummary = !isConsultationSummaryEnabled || !medRequest.data?.questionaire || !!consultationError

  useEffect(() => {
    if (withoutSummary) {
      setConsultationDataAccordionValue('consultation-data')
    }
  }, [withoutSummary])

  if (!parsedQuestions) return null

  const showOnlyUpdatedToggle = (
    <button
      type='button'
      role='switch'
      aria-checked={showOnlyUpdated}
      aria-label='Show only updated answers'
      data-testid='show-only-updated-toggle'
      disabled={!hasUpdatedQuestions}
      title={hasUpdatedQuestions ? 'Show only updated answers' : undefined}
      onClick={() => {
        const next = !showOnlyUpdated
        setShowOnlyUpdated(next)
        if (next) {
          setConsultationDataAccordionValue('consultation-data')
        }
      }}
      className={cn(
        'grid h-8 w-8 shrink-0 place-items-center rounded-[10px] border transition-colors duration-200 cursor-pointer',
        showOnlyUpdated ? 'bg-[#213FB1] border-[#213FB1]' : 'bg-white border-[#D1D9E2]',
        !hasUpdatedQuestions && 'opacity-30 pointer-events-none'
      )}
    >
      <UpdatedBadgeIcon className={cn('h-4 w-4', showOnlyUpdated ? 'text-white' : 'text-[#080F30]')} />
    </button>
  )

  return (
    <div id='consultation' className='flex flex-col gap-[2.3rem]'>
      {isConsultationSummaryEnabled && (
        <div className='flex items-center gap-2'>
          <h2 className='text-custom-neutral-600 text-xl font-semibold leading-[133.4%]'>
            {`Consultation Data${!withoutSummary ? ' Summary' : ''}`}
          </h2>
          {isFetching && <LoadingSpinner iconClassName='w-6 h-6 text-custom-neutral-900 animate-spin' />}
          <Button onClick={invalidate} disabled={isFetching}>
            <RefreshCcw className='w-4 h-4 text-white' />
          </Button>
        </div>
      )}

      {!!consultationError.length && (
        <div className='flex gap-2 items-center'>
          <strong className='text-sm font-bold'>Consultation summary error:</strong>
          <span className='text-red-500 text-sm font-medium font-poppins'>{consultationError}</span>
        </div>
      )}

      {!!mainText.length && (
        <div className='flex flex-col gap-4'>
          <div className='flex items-center bg-[#FAFAFB] border border-[#E9EAED] py-4 px-6 gap-6 rounded-3xl'>
            <InfoRounded className='min-w-6 min-h-6' />
            <span className='text-base leading-7 font-poppins'>
              Please note, this summary has been generated by AI and may be incorrect. Prescribers are clinically
              required to read the full Consultation data before making a decision.
            </span>
          </div>
          <div className='flex flex-col bg-[#FAFAFB] border border-[#E9EAED] p-8 gap-6 rounded-3xl relative'>
            {isFetching && (
              <div className='absolute top-0 left-0 w-full h-full bg-gray-100/10 animate-fade-in backdrop-blur-md grid place-items-center rounded-3xl'>
                <LoadingSpinner iconClassName='w-[80px] h-[80px] text-custom-neutral-900 animate-spin' />
              </div>
            )}
            {!!suggestedDescription && (
              <div className='border border-black/80 p-4 flex flex-col gap-4 rounded-2xl bg-white'>
                <div className='flex items-center gap-2'>
                  <BlackStarIcon />
                  <span className='text-[#1D1D1D] text-base font-semibold font-poppins'>Suggested action</span>
                  <SuggestedActionBadge suggestedActionTag={suggestedActionTag} />
                </div>

                <div className='flex flex-col gap-2'>
                  <span className='text-base font-semibold font-poppins'>{suggestedAction}</span>

                  <span className='text-base font-normal font-poppins'>{suggestedDescription}</span>
                </div>
              </div>
            )}

            <div className='flex flex-col gap-1'>
              {mainText.map((text, idx) => (
                <p
                  key={idx}
                  className={cn(
                    'text-custom-neutral-800 text-sm font-normal leading-5',
                    getAiRespTextClassName({
                      allText: mainText,
                      index: idx
                    })
                  )}
                >
                  {text}
                </p>
              ))}
            </div>

            {attentionText?.length > 0 && (
              <div className='rounded-3xl border border-[#FF8080] flex flex-col'>
                <h3 className='font-poppins font-medium text-base leading-7 px-4 py-3 bg-[#FFE5E5] text-[#1D1D1D] rounded-t-3xl'>
                  Patient flags that may need attention
                </h3>
                <div className='flex flex-col gap-1 p-4'>
                  {attentionText.map((text, idx) => (
                    <p
                      key={idx}
                      className={cn(
                        'text-custom-neutral-800 text-sm font-normal leading-5',
                        getAiRespTextClassName({
                          allText: attentionText,
                          index: idx
                        })
                      )}
                    >
                      {text}
                    </p>
                  ))}
                </div>
              </div>
            )}

            {isConsultationSummaryEnabled && (
              <AiButton data-testid='regenerate-summary' disabled={isFetching} onClick={invalidate}>
                Regenerate summary
              </AiButton>
            )}
          </div>
        </div>
      )}

      <Accordion
        value={consultationDataAccordionValue}
        type='single'
        collapsible
        className='border border-[#D1D9E2] rounded-3xl p-8'
      >
        <AccordionItem value='consultation-data' className='flex flex-col gap-6 border-0'>
          <div className='flex items-center gap-3'>
            <div className='flex-1'>
              <AccordionTrigger
                className='hover:no-underline py-0'
                onClick={e => {
                  e.preventDefault()
                  setConsultationDataAccordionValue(prev => (prev ? undefined : 'consultation-data'))
                }}
              >
                <h2 className='text-custom-neutral-600 text-xl font-semibold leading-[133.4%]'>Consultation Data</h2>
              </AccordionTrigger>
            </div>
            {hasUpdatedQuestions ? (
              showOnlyUpdatedToggle
            ) : (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span tabIndex={0} className='shrink-0 cursor-not-allowed'>
                      {showOnlyUpdatedToggle}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>Consultation has no updated answers</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
          <AccordionContent>
            <ConsultationTable data={parsedQuestions} showOnlyUpdated={showOnlyUpdated} />
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      {isCareVisualisationEnabled && (
        <div id='care-consultation' className='flex flex-col gap-6'>
          <h2 className='text-custom-neutral-600 text-xl font-semibold leading-[133.4%]'>Care Consultation</h2>
          <CareConsultation patientHash={medRequest?.data?.patient?.id} />
        </div>
      )}
    </div>
  )
}
